import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { X, ZoomIn, ZoomOut, RotateCcw, RotateCw, Check } from 'lucide-react';
import getCroppedImg from '../../../utils/canvasUtils';
import styles from './ImageCropModal.module.css';

interface ImageCropModalProps {
    image: string;
    onCropComplete: (croppedImage: Blob) => void;
    onClose: () => void;
}

const AVATAR_STYLES = [
    { id: 'original',  label: 'Original',  filter: '' },
    { id: 'vivid',     label: 'Vivid',     filter: 'saturate(200%) contrast(115%)' },
    { id: 'warm',      label: 'Warm',      filter: 'sepia(45%) saturate(160%) brightness(105%)' },
    { id: 'cool',      label: 'Cool',      filter: 'hue-rotate(190deg) saturate(130%) brightness(105%)' },
    { id: 'grayscale', label: 'Grayscale', filter: 'grayscale(100%)' },
    { id: 'sketch',    label: 'Sketch',    filter: 'grayscale(100%) contrast(220%) brightness(130%)' },
];

const applyFilterToBlob = (blob: Blob, filter: string): Promise<Blob> =>
    new Promise((resolve) => {
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.src = url;
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d')!;
            ctx.filter = filter;
            ctx.drawImage(img, 0, 0);
            URL.revokeObjectURL(url);
            canvas.toBlob((b) => resolve(b!), 'image/jpeg', 0.92);
        };
    });

const ImageCropModal: React.FC<ImageCropModalProps> = ({ image, onCropComplete, onClose }) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [rotation, setRotation] = useState(0);
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
    const [selectedStyleId, setSelectedStyleId] = useState('original');
    const [saving, setSaving] = useState(false);

    const onCropCompleteCallback = useCallback((_: any, pixelCrop: any) => {
        setCroppedAreaPixels(pixelCrop);
    }, []);

    const adjustZoom = (delta: number) =>
        setZoom((z) => Math.min(3, Math.max(1, parseFloat((z + delta).toFixed(1)))));

    const rotateBy = (deg: number) =>
        setRotation((r) => (r + deg + 360) % 360);

    const handleSave = async () => {
        try {
            setSaving(true);
            const croppedBlob = await getCroppedImg(image, croppedAreaPixels, rotation);
            if (!croppedBlob) return;

            const style = AVATAR_STYLES.find((s) => s.id === selectedStyleId);
            if (style && style.filter) {
                const filteredBlob = await applyFilterToBlob(croppedBlob, style.filter);
                onCropComplete(filteredBlob);
            } else {
                onCropComplete(croppedBlob);
            }
        } catch (e) {
            console.error(e);
            setSaving(false);
        }
    };

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContainer}>
                {/* Header */}
                <div className={styles.modalHeader}>
                    <h3>Crop & Style Photo</h3>
                    <button className={styles.closeBtn} onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                {/* Cropper */}
                <div className={styles.cropperContainer}>
                    <Cropper
                        image={image}
                        crop={crop}
                        rotation={rotation}
                        zoom={zoom}
                        aspect={1}
                        onCropChange={setCrop}
                        onRotationChange={setRotation}
                        onCropComplete={onCropCompleteCallback}
                        onZoomChange={setZoom}
                        cropShape="round"
                        showGrid={false}
                        style={{
                            mediaStyle: {
                                filter: AVATAR_STYLES.find(s => s.id === selectedStyleId)?.filter || 'none'
                            }
                        }}
                    />
                </div>

                {/* Controls */}
                <div className={styles.controls}>
                    {/* Zoom */}
                    <div className={styles.controlRow}>
                        <button className={styles.ctrlBtn} onClick={() => adjustZoom(-0.1)} title="Zoom out">
                            <ZoomOut size={16} />
                        </button>
                        <input
                            type="range"
                            value={zoom}
                            min={1}
                            max={3}
                            step={0.1}
                            onChange={(e) => setZoom(Number(e.target.value))}
                            className={styles.rangeInput}
                        />
                        <button className={styles.ctrlBtn} onClick={() => adjustZoom(0.1)} title="Zoom in">
                            <ZoomIn size={16} />
                        </button>
                        <span className={styles.zoomValue}>{zoom.toFixed(1)}×</span>
                    </div>

                    {/* Rotate */}
                    <div className={styles.controlRow}>
                        <button className={styles.ctrlBtn} onClick={() => rotateBy(-1)} title="Rotate left">
                            <RotateCcw size={16} />
                        </button>
                        <input
                            type="range"
                            value={rotation}
                            min={0}
                            max={360}
                            step={1}
                            onChange={(e) => setRotation(Number(e.target.value))}
                            className={styles.rangeInput}
                        />
                        <button className={styles.ctrlBtn} onClick={() => rotateBy(1)} title="Rotate right">
                            <RotateCw size={16} />
                        </button>
                        <span className={styles.zoomValue}>{rotation}°</span>
                    </div>
                </div>

                {/* Style picker */}
                <div className={styles.styleSection}>
                    <p className={styles.styleSectionLabel}>Choose a style</p>
                    <div className={styles.styleRow}>
                        {AVATAR_STYLES.map((style) => (
                            <button
                                key={style.id}
                                className={`${styles.styleCard} ${selectedStyleId === style.id ? styles.selected : ''}`}
                                onClick={() => setSelectedStyleId(style.id)}
                            >
                                <div className={styles.previewWrapper}>
                                    <img
                                        src={image}
                                        alt={style.label}
                                        className={styles.previewImg}
                                        style={{ filter: style.filter || 'none' }}
                                    />
                                    {selectedStyleId === style.id && (
                                        <div className={styles.checkMark}>
                                            <Check size={12} />
                                        </div>
                                    )}
                                </div>
                                <span className={styles.styleLabel}>{style.label}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Footer */}
                <div className={styles.footer}>
                    <button className={styles.cancelBtn} onClick={onClose}>Cancel</button>
                    <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : 'Save Avatar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ImageCropModal;
